// ============================================================================
// DataService — unified data layer (SSoT = server)
// ============================================================================
// Responsibilities:
//   - synchronous in-memory cache reads
//   - optimistic updates
//   - server writes; fallback to SyncEngine queue when offline/error
//   - event stream invalidation
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { syncEngine } from '@/lib/syncEngine';
import { localCache } from '@/lib/localCache';
import { formBindingService } from '@/lib/formBindingService';
import { createLogger } from '@/lib/logger';
import { isTransportError } from '@/lib/errors';
import { markConnectionOffline } from '@/lib/connection';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import type { Row } from '@/lib/storage/persistedCache';

const log = createLogger('DataService');

// ============================================================================
// Helpers
// ============================================================================

/**
 * Связь «онлайн» для записи: сеть устройства + НЕ «Автономный режим» +
 * нет зафиксированного транспортного сбоя (linkDown снимает connection.ts
 * подтверждённым online). Офлайн → запись идёт в очередь SyncEngine.
 */
function isOnline(): boolean {
  return !isEffectivelyOffline();
}

function toRows(records: Record<string, unknown>[]): Row[] {
  return records.map(r => {
    const row: Row = { id: String(r.id ?? '') };
    for (const [k, v] of Object.entries(r)) {
      if (k === 'id') continue;
      if (v === undefined || v === null) {
        row[k] = null;
      } else if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
        row[k] = v;
      } else {
        row[k] = JSON.stringify(v);
      }
    }
    return row;
  });
}

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function tableName(schema: string, table: string): string {
  // In HydraDatacentre schema is usually 'public'; keep full name for universality.
  return schema === 'public' ? table : `${schema}.${table}`;
}

function extractServerVersion(data: Record<string, unknown>): number {
  const v = data.version;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Number.parseInt(v, 10) || 0;
  if (typeof v === 'bigint') return Number(v);
  return 0;
}

// ============================================================================
// Event stream — real-time invalidation (ЕДИНЫЙ хаб eventBusHub: один стрим
// на приложение, топики фильтруются здесь; хаб сам переподключается)
// ============================================================================

let eventStreamActive = false;

async function startEventStream(): Promise<void> {
  if (eventStreamActive) return;
  // Детерминированно: без токена подписка невозможна (401) — не стартуем
  // и не шумим в консоль; стрим запускается после входа (dataService.init).
  const { useCenterStore } = await import('@/store/centerStore');
  if (!useCenterStore.getState().token) return;
  eventStreamActive = true;
  try {
    const { eventBusHub } = await import('@/lib/eventBusHub');
    const topics = new Set([
      'field.created', 'field.updated', 'field.deleted',
      'tabular_section.created', 'tabular_section.updated', 'tabular_section.deleted',
      'form.created', 'form.updated', 'form.deleted',
      'catalog.created', 'catalog.updated', 'catalog.deleted',
      'document.created', 'document.updated', 'document.deleted',
      'register.created', 'register.updated', 'register.deleted',
    ]);
    eventBusHub.subscribe((ev) => {
      if (topics.has(ev.topic)) {
        invalidateCacheForEvent(ev);
      }
    });
  } catch (err) {
    log.warn('Event stream subscribe failed', err);
    eventStreamActive = false;
  }
}

function invalidateCacheForEvent(event: any): void {
  const topic = event?.topic || '';
  const parts = topic.split('.');
  if (parts.length < 2) {
    localCache.clearAll();
    return;
  }

  const [entityType] = parts;

  if (['field', 'tabular_section', 'form'].includes(entityType)) {
    const metadataTables = ['metadata_fields', 'metadata_tabular_sections', 'form_definitions'];
    for (const table of metadataTables) {
      localCache.clear('public', table);
    }
    // Создание/изменение/удаление формы → кэш привязок устарел
    // (иначе вновь созданная кастомная форма не подхватывается — рендерится автоформа).
    if (entityType === 'form') {
      formBindingService.invalidate();
    }
    return;
  }

  const entityPrefix =
    entityType === 'catalog' ? 'metadata_catalogs' :
    entityType === 'document' ? 'metadata_documents' :
    entityType === 'register' ? 'metadata_registers' : null;

  if (entityPrefix) {
    localCache.clear('public', entityPrefix);
    localCache.clearAll();
    return;
  }

  localCache.clearAll();
}

// ============================================================================
// DataService public API
// ============================================================================

export const dataService = {
  localCache,

  subscribe: localCache.subscribe,

  isOnline,

  // === Synchronous reads (from hot cache) ===

  list<T>(schema: string, table: string): T[] {
    return (localCache.get(schema, table) || []) as T[];
  },

  getById<T>(schema: string, table: string, id: string): T | undefined {
    const items = localCache.get(schema, table) || [];
    return items.find((item: any) => item.id === id) as T | undefined;
  },

  // === Server fetch (and cache) ===

  async fetchAll(schema: string, table: string): Promise<void> {
    try {
      const sdk = getSDK();
      const result = await sdk.metadata.listTableData({ schema, table, pageSize: 1000, page: 0 });
      const rows = (result.rows || []).map((r: any) => {
        const flat: Record<string, unknown> = { id: r.id };
        for (const v of r.values || []) {
          const val = v.value === '<nil>' || v.value === '<nil>\n' ? null : v.value;
          flat[v.column] = val;
        }
        return flat;
      });
      localCache.set(schema, table, toRows(rows));
    } catch (e) {
      // Транспортный сбой — связь с сервером потеряна: офлайн-режим
      // (чтение из кэша продолжается; серверные ошибки кэш не трогают).
      if (isTransportError(e)) markConnectionOffline();
      log.error('fetchAll failed', { schema, table, error: e });
      // Do not clear existing cache; offline reads remain possible.
    }
  },

  async fetchById(schema: string, table: string, id: string): Promise<Record<string, unknown> | null> {
    try {
      await this.fetchAll(schema, table);
      return this.getById(schema, table, id) || null;
    } catch (e) {
      log.error('fetchById failed', { schema, table, id, error: e });
      return this.getById(schema, table, id) || null;
    }
  },

  // === Writes (online → server напрямую, ошибки БРОСАЮТСЯ; offline → sync queue) ===

  async create(
    schema: string,
    table: string,
    data: Record<string, unknown>,
    formAttributes?: Record<string, string>
  ): Promise<Record<string, unknown>> {
    const newRecord = { id: generateUUID(), ...data };
    const tbl = tableName(schema, table);

    // Оптимистичное обновление (для offline-отображения и кэша чтения)
    localCache.insert(schema, table, toRows([newRecord])[0]);

    if (isOnline()) {
      try {
        // Онлайн: только сервер. Ошибка СЕРВЕРА — реальная ошибка, НЕ очередь.
        const sdk = getSDK();
        const values = Object.entries(data)
          .filter(([k, v]) => v !== undefined)
          .map(([column, value]) => ({ column, value: String(value ?? '') }));
        const result = await sdk.metadata.createTableRow({ schema, table, values, formAttributes });
        const serverId = result.id;
        if (serverId && serverId !== newRecord.id) {
          // Replace temp id with server id
          localCache.update(schema, table, newRecord.id, { id: serverId } as Partial<Row>);
          newRecord.id = serverId;
        }
        return newRecord;
      } catch (e) {
        if (!isTransportError(e)) throw e;
        // Транспортный сбой (связь потеряна) — офлайн-путь: durable-очередь.
        markConnectionOffline();
      }
    }
    // Офлайн: очередь синхронизации (будет отправлена при восстановлении сети)
    await syncEngine.addToQueue(tbl, newRecord.id, 'create', newRecord, 0, formAttributes);

    return newRecord;
  },

  async update(
    schema: string,
    table: string,
    id: string,
    data: Record<string, unknown>,
    formAttributes?: Record<string, string>
  ): Promise<void> {
    const tbl = tableName(schema, table);
    const existing = localCache.getById(schema, table, id);
    const expectedVersion = existing ? extractServerVersion(existing as Record<string, unknown>) : 0;

    // Оптимистичное обновление
    localCache.update(schema, table, id, data as Partial<Row>);

    if (isOnline()) {
      try {
        // Онлайн: только сервер. Ошибка СЕРВЕРА — реальная ошибка, НЕ очередь.
        const sdk = getSDK();
        const values = Object.entries(data)
          .filter(([k, v]) => v !== undefined)
          .map(([column, value]) => ({ column, value: String(value ?? '') }));
        await sdk.metadata.updateTableRow({ schema, table, rowId: id, values, formAttributes });
        return;
      } catch (e) {
        if (!isTransportError(e)) throw e;
        // Транспортный сбой (связь потеряна) — офлайн-путь: durable-очередь.
        markConnectionOffline();
      }
    }
    await syncEngine.addToQueue(tbl, id, 'update', data, expectedVersion, formAttributes);
  },

  /** ЕДИНОЕ сохранение формы (кастомная форма: {formId, values}) — сервер по
   *  манифесту делит physical/form_attribute. Онлайн → SaveForm RPC (ошибка
   *  БРОСАЕТСЯ); офлайн → очередь sync ('save_form') → SaveForm при синке. */
  async saveForm(formId: string, recordId: string, values: Record<string, string>): Promise<string> {
    if (isOnline()) {
      try {
        // Онлайн: только сервер. Ошибка СЕРВЕРА — реальная ошибка, НЕ очередь.
        const r: any = await getSDK().metadata.saveForm({ formId, recordId, values } as any);
        return String(r?.recordId || r?.id || '');
      } catch (e) {
        if (!isTransportError(e)) throw e;
        // Транспортный сбой (связь потеряна) — офлайн-путь: durable-очередь.
        markConnectionOffline();
      }
    }
    await syncEngine.addToQueue(`form:${formId}`, recordId || generateUUID(), 'save_form', { formId, recordId, values } as unknown as Record<string, unknown>, 0);
    return recordId;
  },

  async remove(schema: string, table: string, id: string): Promise<void> {    const tbl = tableName(schema, table);
    const existing = localCache.getById(schema, table, id);
    const expectedVersion = existing ? extractServerVersion(existing as Record<string, unknown>) : 0;

    // Оптимистичное обновление
    localCache.remove(schema, table, id);

    if (isOnline()) {
      try {
        // Онлайн: только сервер. Ошибка СЕРВЕРА — реальная ошибка, НЕ очередь.
        const sdk = getSDK();
        if (table === 'entities' || table === 'entity') {
          await sdk.entity.deleteEntity({ id });
        } else {
          await sdk.metadata.deleteTableRow({ schema, table, rowId: id });
        }
        return;
      } catch (e) {
        if (!isTransportError(e)) throw e;
        // Транспортный сбой (связь потеряна) — офлайн-путь: durable-очередь.
        markConnectionOffline();
      }
    }
    await syncEngine.addToQueue(tbl, id, 'delete', { id }, expectedVersion);
  },

  // === Invalidation ===

  invalidate(schema: string, table: string): void {
    localCache.clear(schema, table);
  },

  invalidateAll(): void {
    localCache.clearAll();
  },

  // === Initialization ===

  async init(): Promise<void> {
    await localCache.hydrate();
    startEventStream();
  },
};
